import { CalendarDays, CheckCircle, ChevronLeft, ChevronRight, Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Activities from '../../../components/Activities/Activity';
import ErrorModal from '../../../components/ConfirmModal/ErrorModal';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import Header from '../../../components/Header';
import Loader from '../../../components/Loader/Loader';
import RejectButton from '../../../components/RejectButton/RejectButton';
import {
  downloadShiftRoster,
  getOperationManagers,
  getShiftRosterDetail,
  getShiftRosterDutyCodes,
  getShiftRosterGroups,
  markShiftRosterApproved,
  notifyShiftRosterApproval,
  updateShiftRosterCell,
} from '../../../services/shiftRoasterService';
import EditPersonnelModal from './EditPersonnelModal';

const DAYS_PER_PAGE = 7;

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};

const getDayName = (year: number, month: number, date: number) => {
  return DAY_NAMES[new Date(year, month, date).getDay()];
};

const ShiftCalendar = () => {
  const { id } = useParams();

  const today = new Date();

  const [startIndex, setStartIndex] = useState(0);

  const [rosterDetail, setRosterDetail] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const rosterYear = rosterDetail?.year ? Number(rosterDetail.year) : today.getFullYear();
  const rosterMonth = rosterDetail?.month ? Number(rosterDetail.month) - 1 : today.getMonth();

  const [groups, setGroups] = useState<any[]>([]);
  const [dutyCodes, setDutyCodes] = useState<any[]>([]);

  const [showNotifyApprovalModal, setShowNotifyApprovalModal] = useState(false);
  const [operationManagers, setOperationManagers] = useState<any[]>([]);
  const [selectedOperationManager, setSelectedOperationManager] = useState<any>(null);
  const [notifyDropdownOpen, setNotifyDropdownOpen] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [errorModal, setErrorModal] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [errorMessage, setErrorMessage] = useState({
    title: '',
    message: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [groupsRes, dutyCodesRes] = await Promise.all([getShiftRosterGroups(), getShiftRosterDutyCodes()]);

        setGroups(groupsRes.data || []);
        setDutyCodes(dutyCodesRes.data || []);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const fetchRosterDetail = async () => {
    if (!id) return;

    try {
      setLoading(true);

      const response = await getShiftRosterDetail(id);

      const data = response.data;

      setRosterDetail(data);

      const lookup: { [key: string]: string } = {};

      data.details.forEach((item: any) => {
        const day = new Date(item.roster_date).getDate();

        lookup[`${item.group_id}-${day}`] = item.duty_code;
      });

      setCalendarData(lookup);
    } catch (error) {
      console.error('Error fetching roster detail:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRosterDetail();
  }, [id]);

  const handleNotifyApproval = async () => {
    if (!selectedOperationManager) return;

    try {
      setNotifyLoading(true);

      await notifyShiftRosterApproval(id, {
        user_ids: [selectedOperationManager.id],
      });

      setShowNotifyApprovalModal(false);
      setSelectedOperationManager(null);

      await fetchRosterDetail();

      setSuccessMessage('Notification sent successfully.');
      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error: any) {
      const response = error?.response?.data;

      let message = 'Something went wrong.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({
        title: 'Notification Failed',
        message,
      });

      setErrorModal(true);
    } finally {
      setNotifyLoading(false);
    }
  };

  const totalDays = getDaysInMonth(rosterYear, rosterMonth);

  const allDays = Array.from({ length: totalDays }, (_, i) => ({
    date: i + 1,
    day: getDayName(rosterYear, rosterMonth, i + 1),
  }));

  const visibleDays = allDays.slice(startIndex, startIndex + DAYS_PER_PAGE);

  const firstDate = String(visibleDays[0]?.date || 1).padStart(2, '0');

  const lastDate = String(visibleDays[visibleDays.length - 1]?.date || totalDays).padStart(2, '0');

  const dateRange = `${firstDate} - ${lastDate}`;

  const [selectedCell, setSelectedCell] = useState<{ group: any; day: any; x: number; y: number } | null>(null);

  const [showDutyCodes, setShowDutyCodes] = useState(false);

  const [showEditPersonnelModal, setShowEditPersonnelModal] = useState(false);

  const [calendarData, setCalendarData] = useState<{
    [key: string]: string;
  }>({});

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showDownloadConfirmation, setShowDownloadConfirmation] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);

  const currentUserID = Number(localStorage.getItem('currentUserID'));
  const isCreatorNew = Number(rosterDetail?.created_by) === currentUserID && rosterDetail.status === 'New';

  const handlePrevWeek = () => {
    setStartIndex((prev) => Math.max(0, prev - DAYS_PER_PAGE));
  };

  const handleNextWeek = () => {
    if (startIndex + DAYS_PER_PAGE < allDays.length) {
      setStartIndex((prev) => prev + DAYS_PER_PAGE);
    }
  };
  const handleOpenNotifyModal = async () => {
    const hasAssignedDutyCode = Object.values(calendarData).some((value) => value && value.trim() !== '');

    if (!hasAssignedDutyCode) {
      setErrorMessage({
        title: 'Validation Error',
        message: 'Please assign at least one duty code before notifying the Operation Manager.',
      });
      setErrorModal(true);
      return;
    }

    try {
      const response = await getOperationManagers();

      setOperationManagers(response.data || response);
      setSelectedOperationManager(null);
      setNotifyDropdownOpen(false);
      setShowNotifyApprovalModal(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleApprove = async () => {
    if (!id) return;

    try {
      setApproveLoading(true);

      await markShiftRosterApproved(id);

      setShowApproveModal(false);

      await fetchRosterDetail();
    } catch (error: any) {
      const response = error?.response?.data;

      let message = 'Something went wrong.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({
        title: 'Approval Failed',
        message,
      });

      setErrorModal(true);
    } finally {
      setApproveLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!id || !rosterDetail || rosterDetail.status !== 'Approved' || downloadLoading) return;

    try {
      setDownloadLoading(true);

      const pdfBlob = await downloadShiftRoster(id);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      const referenceNumber = rosterDetail.reference_number || rosterDetail.reference_no || `shift-roster-${id}`;

      link.href = url;
      link.download = `${referenceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      setShowDownloadConfirmation(false);
    } catch (error: any) {
      let message = error?.message || 'Failed to download shift roster.';
      const responseData = error?.response?.data;

      if (responseData instanceof Blob) {
        try {
          const parsedResponse = JSON.parse(await responseData.text());
          message = parsedResponse?.message || parsedResponse?.error || message;
        } catch {
          // Keep the fallback message when the Blob is not JSON.
        }
      } else {
        message = responseData?.message || responseData?.error || message;
      }

      setErrorMessage({
        title: 'Download Failed',
        message,
      });
      setShowDownloadConfirmation(false);
      setErrorModal(true);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!id || !rosterDetail || rosterDetail.status !== 'Approved' || printLoading) return;

    try {
      setPrintLoading(true);

      const pdfBlob = await downloadShiftRoster(id);
      const url = window.URL.createObjectURL(pdfBlob);
      const printFrame = document.createElement('iframe');

      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';
      printFrame.src = url;

      printFrame.onload = () => {
        printFrame.contentWindow?.focus();
        printFrame.contentWindow?.print();
        setPrintLoading(false);
      };

      document.body.appendChild(printFrame);

      window.setTimeout(() => {
        printFrame.remove();
        window.URL.revokeObjectURL(url);
      }, 60000);
    } catch (error: any) {
      let message = error?.message || 'Failed to print shift roster.';
      const responseData = error?.response?.data;

      if (responseData instanceof Blob) {
        try {
          const parsedResponse = JSON.parse(await responseData.text());
          message = parsedResponse?.message || parsedResponse?.error || message;
        } catch {
          // Keep the fallback message when the Blob is not JSON.
        }
      } else {
        message = responseData?.message || responseData?.error || message;
      }

      setErrorMessage({
        title: 'Print Failed',
        message,
      });
      setErrorModal(true);
      setPrintLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-white overflow-hidden">
      <Header title="Shift Roster Details " showBackButton />

      {loading || !rosterDetail ? (
        <Loader />
      ) : (
        <>
          <div className="flex flex-1 gap-4 p-6 overflow-hidden">
            {/* Calendar Section */}
            <div className="flex-1 min-w-0 overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {/*Roster Information*/}
              <div className="flex justify-between items-start mb-7">
                <div>
                  {/* Reference + Status */}
                  <div className="flex items-center gap-2">
                    <h2 className="text-[19px] font-semibold text-[#16325C] tracking-tight">
                      {rosterDetail?.reference_number || rosterDetail?.reference_no}
                    </h2>

                    <div className="w-2 h-2 rounded-full bg-[#7C3AED]"></div>

                    <span className="text-[15px] font-medium text-[#7C3AED]">{rosterDetail?.status}</span>
                  </div>

                  {/* Date */}
                  <div className="flex items-center gap-2 mt-1">
                    <CalendarDays size={14} className="text-[#94A3B8]" />

                    <span className="text-[12px] text-[#64748B]">
                      {rosterDetail?.created_at ? new Date(rosterDetail.created_at).toLocaleDateString('en-GB') : ''}
                    </span>
                  </div>

                  {/* Year & Month */}
                  <div className="flex gap-20 mt-5">
                    <div>
                      <p className="text-[12px] text-[#94A3B8]">Year</p>

                      <p className="mt-1 text-[15px] font-semibold text-[#16325C]">{rosterDetail?.year}</p>
                    </div>

                    <div>
                      <p className="text-[12px] text-[#94A3B8]">Month</p>

                      <p className="mt-1 text-[15px] font-semibold text-[#16325C]">{MONTH_NAMES[rosterDetail?.month - 1]}</p>
                    </div>
                  </div>
                </div>

                <div className="ml-6 flex-shrink-0 flex items-center gap-2">
                  {Number(rosterDetail?.created_by) === currentUserID && rosterDetail.status === 'New' && (
                    <button onClick={handleOpenNotifyModal} className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">
                      Notify
                    </button>
                  )}

                  {rosterDetail?.workflow_stages.some((stage) =>
                    stage.approvers.some((approver) => approver.user.id === currentUserID && approver.status === 'PENDING'),
                  ) &&
                    rosterDetail?.status === 'Approval Pending' && (
                      <>
                        <RejectButton title="" id={rosterDetail.id} apiPath="shift-rosters" onSuccess={fetchRosterDetail} />
                        <button onClick={() => setShowApproveModal(true)} className="px-4 py-2 bg-red-600 text-white rounded-md">
                          Approve
                        </button>
                      </>
                    )}

                  {rosterDetail.status === 'Approved' && (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowDownloadConfirmation(true)}
                        disabled={downloadLoading || printLoading}
                        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:bg-red-300 disabled:cursor-not-allowed"
                      >
                        {downloadLoading ? 'Downloading...' : 'Download'}
                      </button>
                      <button
                        type="button"
                        onClick={handlePrint}
                        disabled={printLoading || downloadLoading}
                        title="Print"
                        className="flex items-center gap-2 px-4 py-2 border border-red-500 text-red-500 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Printer size={17} />
                        {printLoading ? 'Preparing...' : 'Print'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Top Bar — now purely month navigation, action buttons moved above */}
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <h2 className="text-base font-semibold min-w-[160px] text-center">
                      {MONTH_NAMES[rosterDetail?.month - 1]}, {rosterDetail?.year}
                    </h2>
                  </div>

                  <div className="flex items-center gap-1">
                    <button onClick={handlePrevWeek} disabled={startIndex === 0} className="p-1 disabled:opacity-30">
                      <ChevronLeft size={16} />
                    </button>

                    <span>{dateRange}</span>

                    <button
                      onClick={handleNextWeek}
                      disabled={startIndex + DAYS_PER_PAGE >= allDays.length}
                      className="p-1 disabled:opacity-30"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="border border-[#e4e9f0] rounded-md overflow-hidden">
                <table className="w-full table-fixed">
                  <thead>
                    <tr>
                      <th className="w-[140px] border border-[#e4e9f0]" />

                      {visibleDays.map((day) => (
                        <th key={day.date} className="border border-[#e4e9f0] py-3">
                          <div className="text-xs text-gray-400">{day.day}</div>

                          <div className="text-lg font-bold">{String(day.date).padStart(2, '0')}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {groups.map((group: any) => (
                      <tr key={group.id}>
                        <td className="border border-[#e4e9f0] px-4 py-4 font-medium">{group.title}</td>
                        {visibleDays.map((day) => {
                          const key = `${group.id}-${day.date}`;
                          return (
                            <td
                              key={day.date}
                              className={`border border-[#e4e9f0] text-center py-4 ${isCreatorNew ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                              onClick={(e) => {
                                if (!isCreatorNew) return;

                                const rect = e.currentTarget.getBoundingClientRect();

                                setSelectedCell({
                                  group,
                                  day,
                                  x: rect.left,
                                  y: rect.bottom,
                                });
                              }}
                            >
                              <span className="text-xl font-bold text-black">{calendarData[key]}</span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap justify-center gap-6 mt-4 text-xs text-gray-500">
                {dutyCodes.map((duty: any) => (
                  <span key={duty.id}>
                    <strong>{duty.code}</strong> - {duty.title} | {duty.start_time.slice(0, 5)} - {duty.end_time.slice(0, 5)}
                  </span>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="w-px bg-gray-200 self-stretch" />

            {/* Activities Sidebar */}
            <div className="md:col-span-1">
              <Activities moduleName="shift_rosters" moduleItemId={Number(id)} />
            </div>
          </div>
          {selectedCell && (
            <div
              className="fixed inset-0 z-50"
              onClick={() => {
                setSelectedCell(null);
                setShowDutyCodes(false);
              }}
            >
              <div
                className="absolute bg-white rounded-lg shadow-xl w-48 border border-gray-200 overflow-hidden"
                style={{ top: selectedCell.y + 4, left: selectedCell.x }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex justify-between items-center px-4 py-3 border-b">
                  <span className="font-semibold text-sm text-[#1a2b4a]">{selectedCell.group.title}</span>
                  <span className="text-sm text-gray-400">
                    {String(selectedCell.day.date).padStart(2, '0')}/{String(rosterMonth + 1).padStart(2, '0')}/{rosterYear}
                  </span>
                </div>

                {/* Duty Code */}
                <div className="relative">
                  <div
                    className="flex justify-between items-center px-4 py-3 border-b bg-blue-50 cursor-pointer hover:bg-blue-100"
                    onClick={() => setShowDutyCodes(!showDutyCodes)}
                  >
                    <span className="text-sm text-gray-700">Duty Code</span>
                    <ChevronRight size={16} className={`text-gray-400 transition-transform ${showDutyCodes ? 'rotate-90' : ''}`} />
                  </div>

                  {showDutyCodes && (
                    <div
                      className="fixed bg-white rounded-lg shadow-xl border border-gray-200 w-48 max-h-40 overflow-y-auto z-[60]"
                      style={{
                        top: selectedCell.y + 50,
                        left: selectedCell.x + 196,
                      }}
                    >
                      {dutyCodes.map((duty: any) => (
                        <div
                          key={duty.id}
                          className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-gray-50 text-sm"
                          onClick={async () => {
                            if (!selectedCell || !id) return;

                            const rosterDate = `${rosterYear}-${String(rosterMonth + 1).padStart(
                              2,
                              '0',
                            )}-${String(selectedCell.day.date).padStart(2, '0')}`;

                            const payload = {
                              shift_roster_id: Number(id),
                              group_id: selectedCell.group.id,
                              roster_date: rosterDate,
                              duty_code_id: duty.id,
                            };

                            try {
                              await updateShiftRosterCell(payload);

                              const key = `${selectedCell.group.id}-${selectedCell.day.date}`;

                              setCalendarData((prev) => ({
                                ...prev,
                                [key]: duty.code,
                              }));

                              setSelectedCell(null);
                              setShowDutyCodes(false);
                            } catch (error: any) {
                              const response = error?.response?.data;

                              let message = 'Something went wrong.';

                              if (response?.errors) {
                                const firstKey = Object.keys(response.errors)[0];

                                if (firstKey) {
                                  message = response.errors[firstKey][0];
                                }
                              } else if (response?.message) {
                                message = response.message;
                              }

                              setErrorMessage({
                                title: 'Update Failed',
                                message,
                              });

                              setErrorModal(true);
                            }
                          }}
                        >
                          <span className="font-semibold text-[#1a2b4a]">{duty.code}</span>
                          <span className="text-gray-500 text-xs">{duty.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Edit Personnel */}
                <div
                  className="px-4 py-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    //setSelectedCell(null);
                    setShowDutyCodes(false);
                    setShowEditPersonnelModal(true);
                  }}
                >
                  <span className="text-sm text-red-500 font-medium">Edit Personnel</span>
                </div>
              </div>
            </div>
          )}

          {showNotifyApprovalModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white w-[480px] rounded-xl shadow-xl p-6 relative">
                <button
                  onClick={() => setShowNotifyApprovalModal(false)}
                  className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
                >
                  ✕
                </button>

                <h2 className="text-lg font-semibold mb-5">Notify Operation Manager</h2>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Operation Manager</label>

                  <div className="relative">
                    <button
                      onClick={() => setNotifyDropdownOpen(!notifyDropdownOpen)}
                      className="w-full flex items-center justify-between border border-gray-300 rounded-md px-3 py-2.5 bg-white text-sm"
                    >
                      {selectedOperationManager ? (
                        selectedOperationManager.name
                      ) : (
                        <span className="text-gray-400">Select Operation Manager</span>
                      )}

                      <span>▼</span>
                    </button>

                    {notifyDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                        {operationManagers.map((manager) => (
                          <button
                            key={manager.id}
                            onClick={() => {
                              setSelectedOperationManager(manager);
                              setNotifyDropdownOpen(false);
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50"
                          >
                            {manager.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowNotifyApprovalModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleNotifyApproval}
                    disabled={!selectedOperationManager || notifyLoading}
                    className={`px-6 py-2 rounded-md text-white ${
                      selectedOperationManager && !notifyLoading ? 'bg-red-500 hover:bg-red-600' : 'bg-red-300 cursor-not-allowed'
                    }`}
                  >
                    {notifyLoading ? 'Sending...' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showApproveModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg w-[400px] p-6 relative shadow-lg">
                <button onClick={() => setShowApproveModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
                  ✕
                </button>

                <h3 className="text-lg font-semibold text-center mb-6">Are you sure you want to approve this shift roster?</h3>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowApproveModal(false)}
                    disabled={approveLoading}
                    className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleApprove}
                    disabled={approveLoading}
                    className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {approveLoading ? 'Processing...' : 'Yes'}
                  </button>
                </div>
              </div>
            </div>
          )}
          <ErrorModal
            isOpen={errorModal}
            onClose={() => setErrorModal(false)}
            title={errorMessage.title}
            message={errorMessage.message}
            buttonText="OK"
            onButtonClick={() => setErrorModal(false)}
          />
          <ConfirmModal
            isOpen={showDownloadConfirmation}
            title="Download Shift Roster"
            message="Are you sure you want to download this shift roster?"
            confirmText="Yes, Download"
            loadingText="Downloading..."
            cancelText="Cancel"
            isLoading={downloadLoading}
            onCancel={() => setShowDownloadConfirmation(false)}
            onConfirm={handleDownload}
          />

          {showSuccess && (
            <div className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-lg shadow-lg flex items-center gap-3 z-[9999]">
              <CheckCircle size={24} />
              <div>
                <p className="font-semibold">Success</p>
                <p>{successMessage}</p>
              </div>
            </div>
          )}

          {showEditPersonnelModal && selectedCell && (
            <EditPersonnelModal
              groupId={selectedCell.group.id}
              shiftRosterId={Number(id)}
              rosterDate={`${rosterYear}-${String(rosterMonth + 1).padStart(2, '0')}-${String(selectedCell.day.date).padStart(2, '0')}`}
              onClose={() => setShowEditPersonnelModal(false)}
              onSuccess={fetchRosterDetail}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ShiftCalendar;
